import { assignInlineVars } from "@vanilla-extract/dynamic";
import { ChangeEvent } from "react";

import { InputTitle } from "chameleon/lib/components";
import {
  CreditCardNetwork,
  DisplayCards,
} from "chameleon/lib/components/display-cards/display-cards";
import { inputWrapperClass } from "chameleon/lib/components/input/input.css";

import { CardNetworkRadioGroupButton } from "./card-network-radio-group-button";
import {
  cardNetworkRadioGroupRootClass,
  cardNetworkRadioOptionsClass,
  gapVar,
} from "./card-network-radio-group.css";

export interface CardNetworkRadioOption {
  title: string;
  value: string;
}

export interface CardNetworkRadioGroupProps {
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Aria Label for accessibility
   */
  ariaLabel: string;
  /**
   * Title for the radio group
   */
  title?: string | null;
  /**
   * Caption for the radio group
   */
  captionMessage?: string;
  /**
   * Info used for the input title's info icon
   */
  info?: string;
  /**
   * Gap between radio elements
   */
  gap?: string;
  /**
   * List of options
   */
  options: CardNetworkRadioOption[];
  /**
   * Selected option
   */
  selectedPaymentType?: CreditCardNetwork[];
  /**
   * Tab index
   */
  tabIndex?: number;
  /**
   * onChange function
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const focusOption = (
  currentElement: HTMLDivElement,
  direction: "next" | "previous"
) => {
  const parentElement =
    currentElement?.parentElement?.parentElement?.parentElement;
  const siblingElement =
    direction === "next"
      ? parentElement?.nextElementSibling
      : parentElement?.previousElementSibling;

  if (!siblingElement) return;

  let elementToFocus: HTMLDivElement;
  if (siblingElement.childNodes?.length === 2) {
    elementToFocus = siblingElement?.children[1]?.firstChild
      ?.firstChild as HTMLDivElement;
  } else {
    elementToFocus = siblingElement?.firstChild?.firstChild
      ?.firstChild as HTMLDivElement;
  }

  elementToFocus?.focus();
};

const createChangeEvent = (
  originalEvent: React.KeyboardEvent<HTMLInputElement>,
  newValue: string
): ChangeEvent<HTMLInputElement> => {
  return {
    ...originalEvent,
    target: {
      ...originalEvent.target,
      value: newValue,
    },
  } as unknown as ChangeEvent<HTMLInputElement>;
};

export const CardNetworkRadioGroup = ({
  id,
  ariaLabel,
  title = "",
  captionMessage = "",
  gap = "4",
  options,
  selectedPaymentType,
  onChange,
}: CardNetworkRadioGroupProps) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentElement = e.target as HTMLDivElement;
    const optionIds = options.map((option) => `${option.value}Option`);
    const currentIndex = options.findIndex(
      (option) => option.value === selectedPaymentType?.[0]
    );

    if (!optionIds.includes(currentElement.id)) {
      return;
    }

    switch (e.code) {
      case "ArrowLeft":
      case "ArrowUp": {
        const previousOptionIndex = currentIndex - 1;
        if (previousOptionIndex >= 0) {
          const { value } = options[previousOptionIndex];
          onChange(createChangeEvent(e, value));

          focusOption(currentElement, "previous");
        }
        break;
      }
      case "ArrowDown":
      case "ArrowRight": {
        const nextOptionIndex = currentIndex + 1;
        if (nextOptionIndex < options.length) {
          const { value } = options[nextOptionIndex];
          onChange(createChangeEvent(e, value));

          focusOption(currentElement, "next");
        }
        break;
      }
      default:
        return;
    }
  };

  return (
    <div className={inputWrapperClass}>
      <div
        role="radiogroup"
        aria-labelledby={ariaLabel}
        className={cardNetworkRadioGroupRootClass}
        style={assignInlineVars({
          [gapVar]: gap,
        })}
      >
        {title && (
          <InputTitle
            secondaryTitle={captionMessage}
            htmlFor={id}
            title={title}
            justifyContent="flex-start"
          />
        )}
        <div className={cardNetworkRadioOptionsClass}>
          {options.map((option) => {
            return (
              <span key={option.value}>
                <CardNetworkRadioGroupButton
                  id={`radio-${option.value}`}
                  key={option.value}
                  value={option.value}
                  type={"radio"}
                  isSelected={
                    option.value === (selectedPaymentType?.[0] as string)
                  }
                  onKeyDown={onKeyDown}
                  onChange={onChange}
                  title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <DisplayCards
                        supportedPaymentTypes={[
                          option.value as CreditCardNetwork,
                        ]}
                        displayCardsSpanStyle={{ padding: "0 10px 0 0" }}
                      />
                      <span style={{ paddingRight: "40px" }}>
                        {option.title}
                      </span>
                    </div>
                  }
                />
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CardNetworkRadioGroup.displayName = "CardNetworkRadioGroup";
